import { rewriteTriggerEdgesInPlace, type TriggerEdgeLike } from '@modules/screen-utils/services/triggerEdgeWalker';
import {
  STRATEGY_BLOCK_DOC_TAG_PREFIX,
  STRATEGY_BLOCKS_MANIFEST_METADATA_KEY,
  STRATEGY_BLOCK_TAG_PREFIX,
  STRATEGY_TAG_PREFIX,
  type StrategyFlowBlock,
  type StrategyFlowScreen,
} from './strategyFlowDocumentTransformer';
import { getBlockEntityId } from './blockNavigationEdges';

export type StrategyScreenTransplantMembership =
  | { mode: 'no-block' }
  | { mode: 'assign-existing-block'; targetBlockEntityId: string }
  | { mode: 'create-new-block'; targetBlockEntityId: string; targetBlockName?: string };

export interface PrepareStrategyScreenTransplantInput {
  sourceStrategyId: string;
  sourceBlock: StrategyFlowBlock;
  selectedScreenIds?: string[];
  membership: StrategyScreenTransplantMembership;
  copiedAt?: number;
}

export interface StrategyScreenTransplantPayload {
  screens: StrategyFlowScreen[];
  originalScreenIds: string[];
  warnings: string[];
  membership: StrategyScreenTransplantMembership;
  source: StrategyScreenTransplantSource;
}

export interface StrategyScreenTransplantSource {
  strategyId: string;
  blockEntityId: string;
  blockDocId?: string;
  selectedScreenIds: string[];
  wholeBlock: boolean;
  copiedAt: number;
}

export interface ApplyStrategyScreenTransplantPastePolicyInput {
  screens: StrategyFlowScreen[];
  source?: StrategyScreenTransplantSource | null;
  targetStrategyId?: string | null;
}

export interface ApplyStrategyScreenTransplantPastePolicyResult {
  screens: StrategyFlowScreen[];
  appliedBlockEntityId?: string;
}

export function prepareStrategyScreenTransplant(
  input: PrepareStrategyScreenTransplantInput,
): StrategyScreenTransplantPayload {
  const warnings: string[] = [];
  const sourceBlockEntityId = getBlockEntityId(input.sourceBlock) || 'source_block';
  const allScreens = Array.isArray(input.sourceBlock.screens) ? input.sourceBlock.screens : [];
  const availableById = new Map(allScreens.map((screen): [string, StrategyFlowScreen] => [getScreenId(screen), screen]).filter(([id]) => Boolean(id)));
  const requestedIds = input.selectedScreenIds?.length
    ? input.selectedScreenIds
    : allScreens.map(getScreenId).filter(Boolean);
  const selectedIdSet = new Set<string>();

  for (const screenId of requestedIds) {
    if (!availableById.has(screenId)) {
      warnings.push(`Source screen ${screenId} was not found in block ${sourceBlockEntityId}.`);
      continue;
    }
    selectedIdSet.add(screenId);
  }

  const selectedScreens = allScreens.filter((screen) => selectedIdSet.has(getScreenId(screen)));
  if (selectedScreens.length === 0) {
    warnings.push(`No screens selected from block ${sourceBlockEntityId}.`);
  }

  const copiedAt = input.copiedAt ?? Date.now();
  const clonedScreens = selectedScreens.map((screen) => {
    const localScreenId = getScreenId(screen);
    const cloned = cloneJson(screen);
    cloned.tags = buildTransplantTags(cloned.tags, input, sourceBlockEntityId);
    cloned.aiMeta = {
      ...(isRecord(cloned.aiMeta) ? cloned.aiMeta : {}),
      source: {
        strategyId: input.sourceStrategyId,
        blockEntityId: sourceBlockEntityId,
        blockDocId: input.sourceBlock.id,
        localScreenId,
        copiedAt,
      },
    };

    if (isRecord(cloned.content)) {
      rewriteTriggerEdgesInPlace(cloned.content, (edge: TriggerEdgeLike) => {
        if (!edge.nextScreenId || selectedIdSet.has(edge.nextScreenId)) {
          return edge;
        }

        warnings.push(`Dropped external transplant edge ${localScreenId} -> ${edge.nextScreenId}.`);
        return null;
      });
    }

    return cloned;
  });

  return {
    screens: clonedScreens,
    originalScreenIds: selectedScreens.map(getScreenId),
    warnings: dedupeStrings(warnings),
    membership: input.membership,
    source: {
      strategyId: input.sourceStrategyId,
      blockEntityId: sourceBlockEntityId,
      blockDocId: input.sourceBlock.id,
      selectedScreenIds: selectedScreens.map(getScreenId),
      wholeBlock: selectedScreens.length === allScreens.length,
      copiedAt,
    },
  };
}

export function applyStrategyScreenTransplantPastePolicy(
  input: ApplyStrategyScreenTransplantPastePolicyInput,
): ApplyStrategyScreenTransplantPastePolicyResult {
  const source = input.source;
  if (!source) {
    return { screens: input.screens };
  }

  const strippedScreens = input.screens.map(stripActiveStrategyMembership);
  const shouldApplyMembership = Boolean(
    source.wholeBlock
    && source.strategyId
    && source.blockEntityId
    && input.targetStrategyId
    && source.strategyId === input.targetStrategyId,
  );

  if (!shouldApplyMembership) {
    return { screens: strippedScreens };
  }

  return {
    appliedBlockEntityId: source.blockEntityId,
    screens: strippedScreens.map((screen) => ({
      ...screen,
      tags: mergeTags(screen.tags, [
        `${STRATEGY_TAG_PREFIX}${source.strategyId}`,
        `${STRATEGY_BLOCK_TAG_PREFIX}${source.blockEntityId}`,
      ]),
    })),
  };
}

export function extractStrategyIdFromStrategyBlocksMetadata(metadata: unknown): string | undefined {
  if (!isRecord(metadata)) return undefined;
  const rawManifest = metadata[STRATEGY_BLOCKS_MANIFEST_METADATA_KEY];
  const manifest = typeof rawManifest === 'string'
    ? safeParseRecord(rawManifest)
    : rawManifest;

  if (!isRecord(manifest)) return undefined;
  const strategyId = manifest.strategyId;
  return typeof strategyId === 'string' && strategyId.trim() ? strategyId.trim() : undefined;
}

function buildTransplantTags(
  existing: unknown,
  input: PrepareStrategyScreenTransplantInput,
  sourceBlockEntityId: string,
): string[] {
  const tags = readTags(existing).filter((tag) => (
    !tag.startsWith(STRATEGY_TAG_PREFIX)
    && !tag.startsWith(STRATEGY_BLOCK_TAG_PREFIX)
    && !tag.startsWith(STRATEGY_BLOCK_DOC_TAG_PREFIX)
  ));

  tags.push(`source-strategy:${input.sourceStrategyId}`);
  tags.push(`source-block:${sourceBlockEntityId}`);

  if (input.membership.mode !== 'no-block') {
    tags.push(`${STRATEGY_BLOCK_TAG_PREFIX}${input.membership.targetBlockEntityId}`);
  }

  return dedupeStrings(tags);
}

function stripActiveStrategyMembership(screen: StrategyFlowScreen): StrategyFlowScreen {
  return {
    ...screen,
    tags: readTags(screen.tags).filter((tag) => (
      !tag.startsWith(STRATEGY_TAG_PREFIX)
      && !tag.startsWith(STRATEGY_BLOCK_TAG_PREFIX)
      && !tag.startsWith(STRATEGY_BLOCK_DOC_TAG_PREFIX)
    )),
  };
}

function mergeTags(existing: unknown, additions: string[]): string[] {
  return dedupeStrings([...readTags(existing), ...additions]);
}

function getScreenId(screen?: StrategyFlowScreen): string {
  if (!screen) return '';
  return String(screen.aiMeta?.localScreenId || screen.id || screen.entityId || '').trim();
}

function readTags(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((tag): tag is string => typeof tag === 'string') : [];
}

function dedupeStrings(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function safeParseRecord(value: string): Record<string, unknown> | undefined {
  try {
    const parsed = JSON.parse(value);
    return isRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function cloneJson<T>(value: T): T {
  return value === undefined ? value : JSON.parse(JSON.stringify(value));
}
